import React, { useState, useEffect } from 'react';
import { 
  ToggleLeft, ToggleRight, Users, CheckSquare, Building2, GitBranch, 
  Send, Workflow, MessageSquare, BarChart, Save, AlertCircle, RefreshCw
} from 'lucide-react';
import { crmModulesAPI } from '../../services/api';

// Icon mapping for modules
const iconMap = {
  'Users': Users,
  'CheckSquare': CheckSquare,
  'Building2': Building2,
  'GitBranch': GitBranch,
  'Send': Send,
  'Workflow': Workflow,
  'MessageSquare': MessageSquare,
  'BarChart': BarChart,
};

const ModuleCard = ({ module, onToggle, saving }) => {
  const IconComponent = iconMap[module.icon] || Building2;
  
  return (
    <div 
      className={`bg-white rounded-xl border-2 transition-all duration-200 ${
        module.is_enabled 
          ? 'border-teal-200 shadow-sm' 
          : 'border-gray-100 opacity-75'
      }`}
      data-testid={`module-card-${module.module_name}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${
              module.is_enabled 
                ? 'bg-teal-50 text-teal-600' 
                : 'bg-gray-100 text-gray-400'
            }`}>
              <IconComponent size={24} />
            </div>
            <div>
              <h3 className={`font-semibold ${
                module.is_enabled ? 'text-gray-800' : 'text-gray-500'
              }`}>
                {module.display_name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {module.description || 'No description available'}
              </p>
            </div>
          </div>
          
          <button
            onClick={() => onToggle(module)}
            disabled={saving}
            className="p-1 transition-transform hover:scale-110 disabled:opacity-50"
            title={module.is_enabled ? 'Click to disable' : 'Click to enable'}
            data-testid={`toggle-module-${module.module_name}`}
          >
            {module.is_enabled ? (
              <ToggleRight size={36} className="text-teal-600" />
            ) : (
              <ToggleLeft size={36} className="text-gray-300" />
            )}
          </button>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              module.is_enabled 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              {module.is_enabled ? 'Enabled' : 'Disabled'}
            </span>
            <span className="text-gray-400">
              Order: {module.order}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModuleManagement = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalModules, setOriginalModules] = useState([]);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await crmModulesAPI.getAll();
      const moduleData = response.data?.data || [];
      setModules(moduleData);
      setOriginalModules(JSON.parse(JSON.stringify(moduleData)));
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError('Failed to load modules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (module) => {
    const updatedModules = modules.map(m => 
      m.id === module.id ? { ...m, is_enabled: !m.is_enabled } : m
    );
    setModules(updatedModules);
    setHasChanges(true);
    
    // Auto-save immediately
    try {
      setSaving(true);
      await crmModulesAPI.update(module.id, { is_enabled: !module.is_enabled });
      // Update original modules after successful save
      setOriginalModules(JSON.parse(JSON.stringify(updatedModules)));
      setHasChanges(false);
    } catch (err) {
      console.error('Error updating module:', err);
      // Revert on error
      setModules(originalModules);
      alert('Failed to update module. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      await crmModulesAPI.bulkUpdate(modules);
      setOriginalModules(JSON.parse(JSON.stringify(modules)));
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving modules:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = modules.filter(m => m.is_enabled).length;
  const disabledCount = modules.filter(m => !m.is_enabled).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="text-red-500 mx-auto mb-3" size={32} />
        <p className="text-red-700 mb-4">{error}</p>
        <button 
          onClick={fetchModules}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          <RefreshCw size={16} className="inline mr-2" />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="module-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Module Management</h1>
          <p className="text-gray-500 mt-1">
            Enable or disable CRM modules. Disabled modules will be hidden from the navigation.
          </p>
        </div>
        
        {hasChanges && (
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            data-testid="save-modules-btn"
          >
            <Save size={18} className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-3xl font-bold text-gray-800">{modules.length}</p>
          <p className="text-sm text-gray-500">Total Modules</p>
        </div>
        <div className="bg-white rounded-xl border border-green-200 p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{enabledCount}</p>
          <p className="text-sm text-gray-500">Enabled</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-3xl font-bold text-gray-400">{disabledCount}</p>
          <p className="text-sm text-gray-500">Disabled</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="text-amber-800 font-medium">Important Note</p>
          <p className="text-amber-700 text-sm mt-1">
            Disabling a module will hide it from the sidebar navigation. Users will not be able to access disabled modules.
            API endpoints for disabled modules will still function to prevent data loss.
          </p>
        </div>
      </div>

      {/* Module Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((module) => (
          <ModuleCard 
            key={module.id}
            module={module}
            onToggle={handleToggle}
            saving={saving}
          />
        ))}
      </div>

      {modules.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500">No modules found.</p>
        </div>
      )}
    </div>
  );
};

export default ModuleManagement;
